import { interpolateFps, type BenchmarkData } from '../lib/fps-interpolation';

type FpsAreaConfig = {
  passmarkScore: number;
  defaultGame: string;
  defaultGameName: string;
  defaultResolution: '1080p' | '1440p' | '4k';
  defaultGraphics: 'low' | 'medium' | 'high' | 'ultra';
};

class GamingFpsArea extends HTMLElement {
  private benchmarkData: BenchmarkData = {};
  private config: FpsAreaConfig | null = null;

  private state = {
    selectedGame: '',
    selectedGameName: '',
    selectedGameImg: '',
    selectedResolution: '1440p',
    selectedGraphics: 'high',
  };

  private valueRowEl: HTMLElement | null = null;
  private valueEl: HTMLElement | null = null;
  private subtitleEl: HTMLElement | null = null;
  private segments: HTMLElement[] = [];
  private resolutionSelect: HTMLSelectElement | null = null;
  private graphicsSelect: HTMLSelectElement | null = null;

  private gameSelectRoot: HTMLElement | null = null;
  private gameButton: HTMLButtonElement | null = null;
  private gameMenu: HTMLElement | null = null;
  private gameImgEl: HTMLImageElement | null = null;
  private gameTextEl: HTMLElement | null = null;

  private onOutsideClick: ((e: MouseEvent) => void) | null = null;
  private onEscape: ((e: KeyboardEvent) => void) | null = null;

  connectedCallback() {
    this.parseData();
    this.cacheDom();
    this.initState();
    this.bindEvents();
    this.updateDisplay();
  }

  disconnectedCallback() {
    this.detachGlobalListeners();
  }

  private parseData() {
    const benchmarkScript = this.querySelector('[data-fps-benchmarks]');
    if (benchmarkScript?.textContent) {
      try {
        this.benchmarkData = JSON.parse(benchmarkScript.textContent);
      } catch {
        /* ignore */
      }
    }

    const configScript = this.querySelector('[data-fps-area-config]');
    if (configScript?.textContent) {
      try {
        this.config = JSON.parse(configScript.textContent);
      } catch {
        this.config = null;
      }
    }
  }

  private cacheDom() {
    this.valueRowEl = this.querySelector('[data-fps-area-value-row]');
    this.valueEl = this.querySelector('[data-fps-area-value]');
    this.subtitleEl = this.querySelector('[data-fps-area-subtitle]');
    this.segments = Array.from(this.querySelectorAll('[data-fps-area-segment]'));
    this.resolutionSelect = this.querySelector('[data-fps-area-resolution]');
    this.graphicsSelect = this.querySelector('[data-fps-area-graphics]');

    this.gameSelectRoot = this.querySelector('[data-fps-area-game-select]');
    this.gameButton = this.querySelector('[data-fps-area-game-button]');
    this.gameMenu = this.querySelector('[data-fps-area-game-menu]');
    this.gameImgEl = this.querySelector('[data-fps-area-game-img]');
    this.gameTextEl = this.querySelector('[data-fps-area-game-text]');
  }

  private initState() {
    if (this.config) {
      this.state.selectedResolution = this.config.defaultResolution;
      this.state.selectedGraphics = this.config.defaultGraphics;
      this.state.selectedGame = this.config.defaultGame;
      this.state.selectedGameName = this.config.defaultGameName;
    }

    const firstOption = this.querySelector<HTMLElement>('[data-fps-area-game-option]');
    if (firstOption) {
      this.state.selectedGame = firstOption.dataset.gameSlug || this.state.selectedGame;
      this.state.selectedGameName =
        firstOption.dataset.gameName || this.state.selectedGameName || this.state.selectedGame;
      this.state.selectedGameImg = firstOption.dataset.gameImg || '';
    }

    if (this.resolutionSelect) this.resolutionSelect.value = this.state.selectedResolution;
    if (this.graphicsSelect) this.graphicsSelect.value = this.state.selectedGraphics;

    this.syncGameButton();
  }

  private bindEvents() {
    this.resolutionSelect?.addEventListener('change', (e) => {
      this.state.selectedResolution = (e.target as HTMLSelectElement).value as typeof this.state.selectedResolution;
      this.updateDisplay();
    });

    this.graphicsSelect?.addEventListener('change', (e) => {
      this.state.selectedGraphics = (e.target as HTMLSelectElement).value as typeof this.state.selectedGraphics;
      this.updateDisplay();
    });

    this.querySelectorAll<HTMLElement>('[data-fps-area-game-option]').forEach((opt) => {
      opt.addEventListener('click', () => {
        this.selectGame(opt.dataset.gameSlug || '', opt.dataset.gameName || '', opt.dataset.gameImg || '');
      });
    });

    this.gameButton?.addEventListener('click', () => {
      this.toggleMenu();
    });
  }

  private selectGame(slug: string, name: string, img: string) {
    if (!slug) return;
    this.state.selectedGame = slug;
    this.state.selectedGameName = name || slug;
    this.state.selectedGameImg = img || '';

    this.syncGameButton();
    this.closeMenu();
    this.updateDisplay();
  }

  private syncGameButton() {
    if (this.gameTextEl) {
      this.gameTextEl.textContent = this.state.selectedGameName || this.state.selectedGame;
    }
    if (this.gameImgEl) {
      const nextSrc = this.state.selectedGameImg || '';
      if (nextSrc) {
        this.gameImgEl.removeAttribute('srcset');
        this.gameImgEl.src = nextSrc;
        this.gameImgEl.alt = this.state.selectedGameName || this.state.selectedGame;
        this.gameImgEl.removeAttribute('hidden');
      } else {
        this.gameImgEl.setAttribute('hidden', '');
      }
    }
  }

  private toggleMenu() {
    const isOpen = !this.gameMenu?.hasAttribute('hidden');
    if (isOpen) this.closeMenu();
    else this.openMenu();
  }

  private openMenu() {
    if (!this.gameMenu) return;
    this.gameMenu.removeAttribute('hidden');
    this.gameSelectRoot?.classList.add('fps-area-game-select--open');
    this.attachGlobalListeners();
  }

  private closeMenu() {
    if (!this.gameMenu) return;
    this.gameMenu.setAttribute('hidden', '');
    this.gameSelectRoot?.classList.remove('fps-area-game-select--open');
    this.detachGlobalListeners();
  }

  private attachGlobalListeners() {
    if (this.onOutsideClick || this.onEscape) return;

    this.onOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (this.gameSelectRoot && !this.gameSelectRoot.contains(target)) {
        this.closeMenu();
      }
    };

    this.onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.closeMenu();
    };

    document.addEventListener('click', this.onOutsideClick);
    document.addEventListener('keydown', this.onEscape);
  }

  private detachGlobalListeners() {
    if (this.onOutsideClick) document.removeEventListener('click', this.onOutsideClick);
    if (this.onEscape) document.removeEventListener('keydown', this.onEscape);
    this.onOutsideClick = null;
    this.onEscape = null;
  }

  private updateDisplay() {
    const fps = this.getFps();
    this.setResultsVisible(fps > 0);
    this.updateValueAndSubtitle(fps);
    this.updateSegments(fps);
  }

  private setResultsVisible(visible: boolean) {
    const toggle = (el: HTMLElement | null) => {
      if (!el) return;
      if (visible) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    };

    toggle(this.valueRowEl);
    toggle(this.subtitleEl);
    toggle(this.querySelector('.fps-area__bar'));
  }

  private getFps(): number {
    const passmarkScore = this.config?.passmarkScore || 0;
    return interpolateFps(
      this.benchmarkData,
      this.state.selectedGame,
      passmarkScore,
      this.state.selectedResolution,
      this.state.selectedGraphics,
    );
  }

  private updateValueAndSubtitle(fps: number) {
    if (fps <= 0) return;

    if (this.valueEl) {
      // Mock shows a range like 180–210 FPS. We derive a short range from the interpolated base.
      if (fps >= 240) {
        this.valueEl.textContent = '240+ FPS';
      } else {
        const upper = Math.min(fps + 30, 240);
        this.valueEl.textContent = `${fps}–${upper} FPS`;
      }
    }

    if (this.subtitleEl) {
      const res = this.state.selectedResolution === '4k' ? '4K' : this.state.selectedResolution;
      const gfx = this.state.selectedGraphics.charAt(0).toUpperCase() + this.state.selectedGraphics.slice(1);
      this.subtitleEl.textContent = `${this.state.selectedGameName || this.state.selectedGame}, ${res}, ${gfx}`;
    }
  }

  private updateSegments(fps: number) {
    if (!this.segments.length) return;

    const maxFps = 240;
    const percentage = fps > 0 ? Math.min(fps / maxFps, 1) : 0;
    const filled = Math.round(percentage * this.segments.length);

    this.segments.forEach((seg, idx) => {
      seg.classList.toggle('fps-area__segment--filled', idx < filled);
      seg.classList.toggle('fps-area__segment--empty', idx >= filled);
    });
  }
}

if (!customElements.get('gaming-fps-area')) {
  customElements.define('gaming-fps-area', GamingFpsArea);
}
